/**
 * Email template formatting for the editor.
 *
 * templateToHtml mirrors api/src/services/emailFormat.ts so the preview shows
 * what the email will look like — keep the two in sync. Its output is built
 * from HTML-escaped text only, so it is safe to render as markup.
 *
 * pastedHtmlToTemplate converts rich text pasted from Word, Outlook or Google
 * Docs into the template's markers (**bold**, "- " bullets, "1. " numbers),
 * since a <textarea> would otherwise drop the formatting on paste.
 */

const BULLET = /^\s*[-*•·▪◦]\s+(.*)$/;
const NUMBERED = /^\s*\d+[.)]\s+(.*)$/;

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function inline(line: string): string {
  return escapeHtml(line).replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
}

export function templateToHtml(text: string): string {
  const out: string[] = [];
  let paragraph: string[] = [];
  let list: 'ul' | 'ol' | null = null;

  const flushParagraph = () => {
    if (paragraph.length > 0) out.push(`<p>${paragraph.join('<br>')}</p>`);
    paragraph = [];
  };
  const closeList = () => {
    if (list) out.push(`</${list}>`);
    list = null;
  };

  for (const line of text.replace(/\r\n?/g, '\n').split('\n')) {
    const bullet = BULLET.exec(line);
    const numbered = bullet ? null : NUMBERED.exec(line);
    const kind = bullet ? 'ul' : numbered ? 'ol' : null;

    if (kind) {
      flushParagraph();
      if (list !== kind) {
        closeList();
        out.push(`<${kind}>`);
        list = kind;
      }
      out.push(`<li>${inline((bullet ?? numbered)?.[1] ?? '')}</li>`);
    } else if (line.trim() === '') {
      flushParagraph();
      closeList();
    } else {
      closeList();
      paragraph.push(inline(line));
    }
  }
  flushParagraph();
  closeList();
  return out.join('');
}

const SKIP = new Set(['STYLE', 'SCRIPT', 'HEAD', 'TITLE', 'META', 'XML']);
const BLOCK = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'TR', 'BLOCKQUOTE', 'UL', 'OL']);

function isBold(el: HTMLElement): boolean {
  const weight = el.style.fontWeight;
  // Google Docs wraps the whole clipboard in <b style="font-weight:normal">.
  if (weight === 'normal' || weight === '400') return false;
  if (weight === 'bold' || Number(weight) >= 600) return true;
  return el.tagName === 'B' || el.tagName === 'STRONG' || /^H[1-6]$/.test(el.tagName);
}

function walk(node: Node, ordinal: { n: number } | null): string {
  if (node.nodeType === Node.TEXT_NODE) return (node.textContent ?? '').replace(/\s+/g, ' ');
  if (!(node instanceof HTMLElement) || SKIP.has(node.tagName)) return '';
  if (node.tagName === 'BR') return '\n';

  const childOrdinal = node.tagName === 'OL' ? { n: 0 } : node.tagName === 'UL' ? null : ordinal;
  let inner = Array.from(node.childNodes)
    .map((child) => walk(child, childOrdinal))
    .join('');

  if (isBold(node) && inner.trim() && !inner.includes('\n')) {
    const lead = inner.match(/^\s*/)?.[0] ?? '';
    const trail = inner.match(/\s*$/)?.[0] ?? '';
    inner = `${lead}**${inner.trim()}**${trail}`;
  }

  if (node.tagName === 'LI') {
    const marker = ordinal ? `${++ordinal.n}. ` : '- ';
    return `\n${marker}${inner.trim()}\n`;
  }
  return BLOCK.has(node.tagName) ? `\n${inner}\n` : inner;
}

export function pastedHtmlToTemplate(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const lines = walk(doc.body, null)
    .split('\n')
    .map((line) =>
      line
        .replace(/\u00a0/g, ' ')
        .trim()
        // Word's list glyphs (\u00b7 from the Symbol font, \u25aa, \u25e6\u2026) \u2192 a plain "- ".
        .replace(/^[\u2022\u00b7\u25aa\u25e6]\s+/, '- ')
    );

  // Word pastes each list item as its own paragraph — drop the blank lines
  // between consecutive list items so they stay one list.
  const isListLine = (line: string | undefined) => !!line && (BULLET.test(line) || NUMBERED.test(line));
  const kept = lines.filter((line, i) => {
    if (line !== '') return true;
    const prev = lines.slice(0, i).reverse().find((l) => l !== '');
    const next = lines.slice(i + 1).find((l) => l !== '');
    return !(isListLine(prev) && isListLine(next));
  });

  return kept
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
